import { useState } from "react";
import { CheckCircleOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import { Badge } from "antd";
import Button from "../../../Helpers/Button/Button";
import trans, { formatDateTime, isRTL } from "../../../../utils";
import usePermission from "../../../../hooks/usePermission";
import FormSubmissionVersionEditPopup from "./FormSubmissionVersionEditPopup";
import SupervisorSubmitFormPopup from "./SupervisorSubmitFormPopup";

function ProgressBar({ completed, total }) {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="forms-progress-container">
      <div className="forms-progress-header">
        <p className="fs-16 fw-700 brand-700">{trans("Forms Progress")}</p>
        <p className="forms-progress-info">
          {completed}/{total} {trans("completed")} ({percentage}%)
        </p>
      </div>

      <div className="progress">
        <div
          className="progress-bar"
          role="progressbar"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function getVersionLabel(submission) {
  const version = submission.versionNumber || 1;
  return version === 1 ? trans("Original") : `${trans("Update")} ${version - 1}`;
}

function FormHistoryTable({
  submissions,
  setFormSubmissionPopup,
  setEditSubmission,
  canEditSubmission,
}) {
  return (
    <div className="table-responsive" style={{ overflowX: "auto" }}>
      <table
        className="table table-hover table-sm mb-0"
        style={{ minWidth: 600 }}
      >
        <thead>
          <tr>
            <th style={{ width: "20%" }}>{trans("Version")}</th>
            <th style={{ width: "15%" }}>{trans("Status")}</th>
            <th style={{ width: "20%" }}>{trans("Submitted By")}</th>
            <th style={{ width: "20%", whiteSpace: "nowrap" }}>
              {trans("Date & Time")}
            </th>
            <th style={{ width: "25%" }}>{trans("Actions")}</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((submission) => {
            const isOriginal =
              !submission.parentSubmissionId &&
              (submission.versionNumber || 1) === 1;
            return (
              <tr key={submission.id}>
                <td>
                  <span className="fw-600">{getVersionLabel(submission)}</span>
                </td>
                <td>{trans(submission.status)}</td>
                <td>{submission.submittedBy?.name || "-"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {formatDateTime(submission.submittedAt)}
                </td>
                <td>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      type="default"
                      text={trans("View")}
                      onClick={() => setFormSubmissionPopup(submission.id)}
                    />
                    {canEditSubmission && isOriginal && (
                      <Button
                        type="primary"
                        text={trans("Edit")}
                        onClick={() => setEditSubmission(submission)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NoSubmissionState({ canEditSubmission, onSubmitForm }) {
  return (
    <div className="w-100 p-3 text-center">
      <p className="text-muted mb-2">{trans("This form has not been submitted yet")}</p>
      {canEditSubmission && (
        <Button
          type="primary"
          text={trans("Submit Form")}
          onClick={onSubmitForm}
        />
      )}
    </div>
  );
}

function FormCard({
  form,
  taskId,
  setFormPopup,
  setFormSubmissionPopup,
  setEditSubmission,
  setSubmitForm,
  canEditSubmission,
}) {
  const [expanded, setExpanded] = useState(false);
  const submissions = form?.submissions || [];
  const submissionCount = submissions.length;

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <div className="form-card">
      <div className="w-100">
        <div className="d-flex align-items-center justify-content-between w-100">
        <div
          className="d-flex align-items-center gap-3 flex-grow-1"
          style={{ cursor: "pointer" }}
          onClick={toggleExpanded}
        >
          <div className="form-card-icon">
            <img src={`${process.env.PUBLIC_URL}/assets/files.svg`} alt="" />
          </div>
          <div>
            <p className="form-card-name mb-0">
              {form.order}- {form.form?.name || trans("No Data")}
            </p>
            {submissionCount > 0 && (
              <Badge
                count={`${submissionCount} ${
                  submissionCount === 1 ? trans("version") : trans("versions")
                }`}
                style={{
                  backgroundColor: "#0f6936",
                  color: "#fff",
                  fontSize: 11,
                }}
              />
            )}
          </div>
        </div>

        <div className="form-card-actions d-flex align-items-center gap-2">
          {form.isSubmitted && (
            <div className="form-completed-icon">
              <CheckCircleOutlined
                style={{ fontSize: "16px", color: "#0f6936" }}
              />
            </div>
          )}
          <Button
            type={"default"}
            onClick={(e) => {
              e.stopPropagation();
              if (form.isSubmitted) {
                setFormSubmissionPopup(submissions?.[0]?.id);
              } else {
                setFormPopup(form?.formId);
              }
            }}
            text={trans("View Details")}
          />
          <Button
            type="default"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            text={expanded ? <DownOutlined /> : <RightOutlined />}
          />
        </div>
      </div>

        {expanded && (
          <div
            className="w-100 mt-3 pt-3"
            style={{
              borderTop: "1px solid #f1f2f3",
              backgroundColor: "#fafafa",
              borderRadius: 8,
              padding: 16,
            }}
          >
            <p className="fs-14 fw-600 brand-700 mb-2">
              {trans("Submission History")}
            </p>
            {submissionCount > 0 ? (
              <FormHistoryTable
                submissions={submissions}
                setFormSubmissionPopup={setFormSubmissionPopup}
                setEditSubmission={setEditSubmission}
                canEditSubmission={canEditSubmission}
              />
            ) : (
              <NoSubmissionState
                canEditSubmission={canEditSubmission}
                onSubmitForm={() => setSubmitForm(form)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function LinkedForms({
  data,
  setFormPopup,
  formPopup,
  setFormSubmissionPopup,
  onRefresh,
}) {
  const forms = data?.forms || [];
  const completedForms = forms.filter((form) => form.isSubmitted).length;
  const [editSubmission, setEditSubmission] = useState(null);
  const [submitForm, setSubmitForm] = useState(null);
  const { edit: canEditSubmission } = usePermission("FIELD_TASKS");

  const handleEditSuccess = () => {
    setEditSubmission(null);
    onRefresh?.();
  };

  const handleSubmitSuccess = () => {
    setSubmitForm(null);
    onRefresh?.();
  };

  return (
    <div className="w-100 p-3" dir={isRTL ? "rtl" : "ltr"}>
      <ProgressBar completed={completedForms} total={forms.length} />

      <div className="mt-4">
        <p className="fs-16 fw-700 brand-700 mb-2">{trans("Linked Forms")}</p>
        <div className="row g-3">
          {forms.map((form) => (
            <div key={form?.form?.id} className="col-12">
              <FormCard
                form={form}
                taskId={data?.id}
                setFormPopup={setFormPopup}
                setFormSubmissionPopup={setFormSubmissionPopup}
                setEditSubmission={setEditSubmission}
                setSubmitForm={setSubmitForm}
                canEditSubmission={canEditSubmission}
              />
            </div>
          ))}
        </div>
      </div>

      {editSubmission && (
        <FormSubmissionVersionEditPopup
          submission={editSubmission}
          onClose={() => setEditSubmission(null)}
          onSuccess={handleEditSuccess}
          zIndex={1400}
        />
      )}

      {submitForm && (
        <SupervisorSubmitFormPopup
          taskId={data?.id}
          formId={submitForm?.formId}
          formName={submitForm?.form?.name}
          onClose={() => setSubmitForm(null)}
          onSuccess={handleSubmitSuccess}
          zIndex={1400}
        />
      )}
    </div>
  );
}
